import { initializeApp } from "firebase/app";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import React, { createContext } from "react";

import {
  createUserWithEmailAndPassword,
  getAuth,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";

import algoliasearch from "algoliasearch";

const searchAPI = process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY;
const searchApp = process.env.REACT_APP_ALGOLIA_APP_ID;
const searchIndex = process.env.REACT_APP_ALGOLIA_INDEX_NAME;

const client = algoliasearch(searchApp, searchAPI);

const index = client.initIndex(searchIndex);

let prodConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// let devConfig = {
//   apiKey: process.env.REACT_APP_FIREBASE_API_KEY_DEV,
//   authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN_DEV,
//   projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID_DEV,
//   storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_DEV,
//   messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID_DEV,
//   appId: process.env.REACT_APP_FIREBASE_APP_ID_DEV,
// };

// we create a React Context, for this to be accessible
// from a component later
export const FirebaseContext = createContext({
  app: {},
  auth: {},
  database: {},
  api: {},
  storage: {},
});

async function fetchSubcollection(db, docRef, subcollectionName) {
  const subcollectionData = [];

  // Get the subcollection snapshot
  const subcollectionSnapshot = await getDocs(
    collection(db, `${docRef.path}/${subcollectionName}`)
  );

  // Iterate over each document in the subcollection
  for (const subDoc of subcollectionSnapshot.docs) {
    const subDocData = subDoc.data();
    const subDocEntry = {
      path: docRef.path,
      id: subDoc.id,
      ...subDocData,
    };

    // Check if this document has a 'folders' subcollection
    if (subDocData.hasOwnProperty("type") && subDocData.type === "folder") {
      // Recursively fetch data from the 'folders' subcollection
      subDocEntry.folders = await fetchSubcollection(db, subDoc.ref, "folders");

      // Recursively fetch data from the 'docs' subcollection
      subDocEntry.docs = await fetchSubcollection(db, subDoc.ref, "docs");
    }
    subcollectionData.push(subDocEntry);
  }

  return subcollectionData;
}

async function fetchCollection(db, collectionName, subcollections) {
  const collectionData = [];
  const collectionSnapshot = await getDocs(collection(db, collectionName));

  for (const doc of collectionSnapshot.docs) {
    const docData = doc.data();
    const docEntry = {
      id: doc.id,
      path: doc.ref.path,
      ...docData,
    };

    for (const subcollectionName of subcollections) {
      docEntry[subcollectionName] = await fetchSubcollection(
        db,
        doc.ref,
        subcollectionName
      );
    }
    collectionData.push(docEntry);
  }
  return collectionData;
}

const firebaseApp = ({ children, ...props }) => {
  const config = prodConfig;
  // const config = process.env.NODE_ENV === "production" ? prodConfig : devConfig;

  const app = initializeApp(config);
  const auth = getAuth(app);
  const database = getFirestore(app);
  const storage = getStorage();

  const getFiles = async () => {
    const data = await fetchCollection(database, "folders", [
      "folders",
      "docs",
      "links",
    ]);

    return data;
  };

  const getUserRole = async (id) => {
    const docRef = doc(database, "users", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return { role: "basic" };
    }
  };

  const signInWithEmailAndPasswordFunction = async (
    email,
    password,
    teamPassword
  ) => {
    try {
      if (teamPassword === "0001") {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        if (teamPassword === "") {
          alert("You must enter a team password before logging in");
        } else {
          alert("Incorrect team password; try again");
        }
      }
    } catch (err) {
      console.log(err);
      alert(err);
    }
  };

  const sendPasswordReset = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      alert("Password reset link sent!");
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  const logout = () => {
    auth.signOut();
  };

  const registerWithEmailAndPassword = async (
    email,
    password,
    teamPassword
  ) => {
    try {
      if (teamPassword === "0001") {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        if (teamPassword === "") {
          throw new Error(
            "You must enter a team password before creating an account"
          );
        } else {
          throw new Error("Incorrect team password; try again");
        }
      }
    } catch (err) {
      console.log(err);
      alert(err);
    }
  };

  async function uploadFile(file) {
    const storageRef = ref(storage, file.name);
    await uploadBytes(storageRef, file);

    const url = await getDownloadURL(ref(storage, file.name));
    return url;
  }

  // function to query Errors from the database
  async function getErrors() {
    const querySnapshot = await getDocs(collection(database, "errors"));
    let _records = [];
    querySnapshot.forEach((doc) => {
      _records.push({
        ...doc.data(),
        id: doc.id,
      });
    });
    return _records;
  }

  async function addError(payload) {
    try {
      const newDocRef = doc(collection(database, "errors"));
      await setDoc(newDocRef, {
        ...payload,
        id: newDocRef.id,
      });

      return { success: true };
    } catch (e) {
      return { success: false };
    }
  }

  async function removeError(id) {
    try {
      await deleteDoc(doc(database, "errors", id));
    } catch (e) {
      console.log(e);
    }
  }

  async function addFile(payload, image, path) {
    if (image) {
      payload.name = image.name;
      const file = await uploadFile(image);
      payload.url = file;
    }

    try {
      const newDocRef = doc(collection(database, path));
      await setDoc(newDocRef, {
        ...payload,
        id: newDocRef.id,
      });
      return { success: true };
    } catch (e) {
      return { success: false, error: e };
    }
  }

  async function addLink(payload, path) {
    try {
      const newDocRef = doc(collection(database, path));
      await setDoc(newDocRef, {
        ...payload,
        id: newDocRef.id,
      });
      return { success: true };
    } catch (e) {
      return { success: false, error: e };
    }
  }

  async function addFolder(payload, path) {
    function removeTrailingSlash(str) {
      if (str.endsWith("/")) {
        return str.slice(0, -1);
      }
      return str;
    }
    const newPath = removeTrailingSlash(path);

    try {
      const newDocRef = doc(collection(database, `${newPath}/folders`));
      await setDoc(newDocRef, {
        ...payload,
        id: newDocRef.id,
      });
      return { success: true };
    } catch (e) {
      console.log(e);
      return { success: false, error: e };
    }
  }

  async function removeFile(id, path) {
    try {
      await deleteDoc(doc(database, path, id));
    } catch (e) {
      console.log(e);
    }
  }

  async function removeLink(id, path) {
    try {
      await deleteDoc(doc(database, path, id));
    } catch (e) {
      console.log(e);
    }
  }

  async function removeFolder(path) {
    try {
      await deleteDoc(doc(database, path));
    } catch (e) {
      console.log(e);
    }
  }

  async function addEmployee(payload, image) {
    if (image) {
      const file = await uploadFile(image);
      payload.file = file;
    }

    try {
      const newDocRef = doc(collection(database, "employees"));
      await setDoc(newDocRef, {
        ...payload,
        id: newDocRef.id,
      });

      return { success: true };
    } catch (e) {
      return { success: false, error: e };
    }
  }

  async function recieveEmployee(payload) {
    try {
      await setDoc(doc(database, "employees", payload.id), {
        ...payload,
      });
    } catch (e) {
      console.log(e);
    }
  }

  async function updateEmployee(payload, image) {
    if (image) {
      const file = await uploadFile(image);
      payload.file = file;
    }

    try {
      await updateDoc(doc(database, "employees", payload.id), {
        ...payload,
      });
    } catch (e) {
      console.log(e);
    }
  }

  async function removeEmployee(id) {
    try {
      await deleteDoc(doc(database, "employees", id));
    } catch (e) {
      console.log(e);
    }
  }

  async function search(searchTerm) {
    try {
      const results = await index.search(searchTerm, {
        attributesToRetrieve: ["objectID"],
        hitsPerPage: 100,
      });

      return results?.hits;
    } catch (e) {
      console.log(e);
    }
  }

  let firebaseObject = {
    app,
    database,
    auth,
    api: {
      getUserRole,
      getErrors,
      addError,
      removeError,
      getFiles,
      addFile,
      removeFile,
      addLink,
      removeLink,
      addFolder,
      removeFolder,
      addEmployee,
      updateEmployee,
      recieveEmployee,
      removeEmployee,
      onSnapshot,
      query,
      collection,
      signInWithEmailAndPasswordFunction,
      registerWithEmailAndPassword,
      sendPasswordReset,
      logout,
      search,
    },
  };

  return (
    <FirebaseContext.Provider value={firebaseObject}>
      {children}
    </FirebaseContext.Provider>
  );
};

export default firebaseApp;
